import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut,
  RefreshCw,
  Search,
  Download,
  LayoutDashboard,
  Users,
  ShoppingCart,
  GraduationCap,
  Ticket,
  BarChart3,
  ExternalLink,
  IndianRupee,
  Calendar,
  Sparkles,
  TrendingUp,
  Award,
  Plus,
  Pencil,
  Trash2,
  Power,
  AlertTriangle,
  Clock,
  Mail,
  BookOpen,
  Dumbbell,
  Quote,
  Package,
  Settings,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import CoursesManager from "@/components/admin/CoursesManager";
import PricingManager from "@/components/admin/PricingManager";
import PackagesManager from "@/components/admin/PackagesManager";
import StudentsManager from "@/components/admin/StudentsManager";
import EmailLogs from "@/components/admin/EmailLogs";
import TrainingManager from "@/components/admin/TrainingManager";
import TestimonialsManager from "@/components/admin/TestimonialsManager";
import CertificationManager from "@/components/admin/CertificationManager";
import SettingsManager from "@/components/admin/SettingsManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function inr(paise) {
  if (paise == null) return "—";
  return `₹${(paise / 100).toLocaleString("en-IN")}`;
}

function rupees(n) {
  if (n == null) return "—";
  return `₹${Number(n).toLocaleString("en-IN")}`;
}

function downloadCsv(rows, filename) {
  if (!rows.length) {
    toast.error("Nothing to export");
    return;
  }
  const headers = Object.keys(rows[0]);
  const esc = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function useAdminGet(path, deps = []) {
  const [data, setData] = useState(Array.isArray(initialFor(path)) ? [] : null);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}${path}`, { withCredentials: true });
      setData(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("sp-admin-user");
        window.location.href = "/admin/login";
      } else {
        toast.error(`Failed: ${path}`);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, deps);
  return { data, loading, refresh };
}

function initialFor(path) {
  if (path.includes("/stats")) return {};
  return [];
}

const NAV = [
  { id: "overview", label: "Dashboard", icon: LayoutDashboard },
  { id: "leads", label: "Leads CRM", icon: Users },
  { id: "orders", label: "Sales / Orders", icon: ShoppingCart },
  { id: "students", label: "Students", icon: GraduationCap },
  { id: "courses", label: "Courses & Videos", icon: BookOpen },
  { id: "training", label: "Training", icon: Dumbbell },
  { id: "testimonials", label: "Testimonials", icon: Quote },
  { id: "certification", label: "Certification", icon: Award },
  { id: "pricing", label: "Pricing", icon: IndianRupee },
  { id: "packages", label: "Packages", icon: Package },
  { id: "emails", label: "Email Logs", icon: Mail },
  { id: "settings", label: "Settings", icon: Settings },
  { id: "recovery", label: "Recovery", icon: AlertTriangle },
  { id: "coupons", label: "Coupons", icon: Ticket },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
];

function Sidebar({ active, onSelect, onLogout, user }) {
  return (
    <aside
      data-testid="admin-sidebar"
      className="hidden lg:flex w-64 flex-none border-r border-slate-200/70 dark:border-white/5 bg-white/60 dark:bg-[#0F172A]/60 backdrop-blur flex-col"
    >
      <Link to="/" data-testid="admin-brand" className="flex items-center gap-2.5 px-6 py-5 border-b border-slate-200/70 dark:border-white/5">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#0F172A] text-white dark:bg-white dark:text-[#0F172A] font-display font-bold">
          S
        </span>
        <div className="leading-none">
          <div className="font-display font-semibold tracking-tight text-[15px]">SkillPreneur</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Admin
          </div>
        </div>
      </Link>

      <nav className="flex-1 px-3 py-5 space-y-1">
        {NAV.map((n) => {
          const Icon = n.icon;
          const isActive = active === n.id;
          return (
            <button
              key={n.id}
              type="button"
              data-testid={`admin-nav-${n.id}`}
              onClick={() => onSelect(n.id)}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive
                  ? "bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] shadow-sm"
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5"
              }`}
            >
              <Icon className="h-4 w-4" />
              {n.label}
            </button>
          );
        })}
      </nav>

      <div className="px-4 py-4 border-t border-slate-200/70 dark:border-white/5">
        <div className="text-xs text-slate-500 dark:text-slate-400 mb-2 truncate">
          {user?.email}
        </div>
        <button
          type="button"
          onClick={onLogout}
          data-testid="admin-logout"
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-2 text-xs hover:bg-slate-100 dark:hover:bg-white/5"
        >
          <LogOut className="h-3.5 w-3.5" /> Logout
        </button>
      </div>
    </aside>
  );
}

function StatCard({ label, value, icon: Icon, tone = "default", testid }) {
  const tones = {
    default: "text-[#2563EB] dark:text-[#06B6D4]",
    emerald: "text-emerald-600 dark:text-emerald-400",
    amber: "text-amber-600 dark:text-amber-400",
    rose: "text-rose-600 dark:text-rose-400",
  };
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-5 hairline transition-shadow hover:shadow-[0_10px_30px_-15px_rgba(15,23,42,0.2)]"
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
          {label}
        </span>
        <Icon className={`h-4 w-4 ${tones[tone]}`} />
      </div>
      <div className="mt-3 font-display text-3xl font-semibold tracking-tighter text-[#0F172A] dark:text-white">
        {value}
      </div>
    </div>
  );
}

function Table({ cols, rows, loading, empty, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] hairline overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-slate-50/80 dark:bg-white/[0.04]">
            <tr>
              {cols.map((c) => (
                <th key={c} className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-white/5">
            {loading && (
              <tr>
                <td colSpan={cols.length} className="px-5 py-8 text-center text-slate-500 dark:text-slate-400 text-sm">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={cols.length} className="px-5 py-8 text-center text-slate-500 dark:text-slate-400 text-sm">
                  {empty}
                </td>
              </tr>
            )}
            {!loading && rows.map((r, i) => (
              <tr key={i} className="hover:bg-slate-50/60 dark:hover:bg-white/[0.03]">
                {r.map((cell, j) => (
                  <td key={j} className="px-5 py-3 text-slate-700 dark:text-slate-200">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CouponForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    code: initial?.code || "",
    discount_type: initial?.discount_type || "percent",
    discount_value: initial?.discount_value || 10,
    applies_to: initial?.applies_to || "all",
    max_uses: initial?.max_uses || 0,
    expires_at: initial?.expires_at?.slice(0, 10) || "",
    active: initial?.active !== false,
  });
  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : (name === "discount_value" || name === "max_uses" ? Number(value) : value) }));
  };
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      code: form.code.trim().toUpperCase(),
      expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59Z").toISOString() : null,
    };
    await onSave(payload);
  };
  return (
    <form
      onSubmit={submit}
      data-testid="coupon-form"
      className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
    >
      <div className="sm:col-span-2 lg:col-span-3 flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold">{initial ? "Edit Coupon" : "Create New Coupon"}</h3>
        <button type="button" onClick={onCancel} className="text-xs text-slate-500 hover:text-rose-500">
          Cancel
        </button>
      </div>
      <label className="flex flex-col gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
        Code
        <input
          required
          name="code"
          value={form.code}
          onChange={onChange}
          data-testid="coupon-input-code"
          placeholder="LAUNCH50"
          className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2.5 text-sm uppercase tracking-wider font-bold focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
        />
      </label>
      <label className="flex flex-col gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
        Discount type
        <select
          name="discount_type"
          value={form.discount_type}
          onChange={onChange}
          className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2.5 text-sm"
        >
          <option value="percent">Percent (%)</option>
          <option value="fixed">Fixed amount (paise)</option>
        </select>
      </label>
      <label className="flex flex-col gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
        Discount value
        <input
          type="number"
          name="discount_value"
          value={form.discount_value}
          onChange={onChange}
          data-testid="coupon-input-value"
          min={1}
          className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2.5 text-sm"
        />
      </label>
      <label className="flex flex-col gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
        Applies to
        <select
          name="applies_to"
          value={form.applies_to}
          onChange={onChange}
          className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2.5 text-sm"
        >
          <option value="all">All products</option>
          <option value="elite">E-Lite Package</option>
          <option value="basic">Basic Package</option>
          <option value="premium">Premium Package</option>
          <option value="courses">Individual courses</option>
        </select>
      </label>
      <label className="flex flex-col gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
        Max uses (0 = unlimited)
        <input
          type="number"
          name="max_uses"
          value={form.max_uses}
          onChange={onChange}
          min={0}
          className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2.5 text-sm"
        />
      </label>
      <label className="flex flex-col gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
        Expires on
        <input
          type="date"
          name="expires_at"
          value={form.expires_at}
          onChange={onChange}
          className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2.5 text-sm"
        />
      </label>
      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
        <input
          type="checkbox"
          name="active"
          checked={form.active}
          onChange={onChange}
          className="h-4 w-4 rounded"
        />
        Active
      </label>
      <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
        <button
          type="submit"
          data-testid="coupon-save"
          className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-5 py-2.5 text-sm font-semibold hover:-translate-y-0.5 transition-transform"
        >
          {initial ? "Save changes" : "Create coupon"}
        </button>
      </div>
    </form>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [active, setActive] = useState("overview");
  const [search, setSearch] = useState("");
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [showCouponForm, setShowCouponForm] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/me`, { withCredentials: true }).catch(() => nav("/admin/login"));
  }, [nav]);

  const stats = useAdminGet("/admin/stats");
  const leads = useAdminGet("/admin/leads");
  const orders = useAdminGet("/admin/orders");
  const coupons = useAdminGet("/admin/coupons");
  const abandoned = useAdminGet("/admin/abandoned-checkouts");
  const failed = useAdminGet("/admin/failed-payments");

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("sp-admin-user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
    localStorage.removeItem("sp-admin-user");
    nav("/admin/login");
  };

  const filter = (rows, keys) =>
    !search.trim()
      ? rows
      : rows.filter((r) =>
          keys.some((k) =>
            String(r[k] || "").toLowerCase().includes(search.toLowerCase()),
          ),
        );

  const refreshAll = () => {
    stats.refresh();
    leads.refresh();
    orders.refresh();
    coupons.refresh();
    abandoned.refresh();
    failed.refresh();
    toast.success("Refreshed");
  };

  const s = stats.data || {};
  const paidOrders = (orders.data || []).filter((o) => o.status === "paid");

  const handleCouponSave = async (payload) => {
    try {
      if (editingCoupon) {
        await axios.patch(`${API}/admin/coupons/${editingCoupon.id}`, payload, { withCredentials: true });
        toast.success("Coupon updated");
      } else {
        await axios.post(`${API}/admin/coupons`, payload, { withCredentials: true });
        toast.success("Coupon created");
      }
      setEditingCoupon(null);
      setShowCouponForm(false);
      coupons.refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save coupon");
    }
  };

  const handleCouponDelete = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    try {
      await axios.delete(`${API}/admin/coupons/${id}`, { withCredentials: true });
      toast.success("Coupon deleted");
      coupons.refresh();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleCouponToggle = async (c) => {
    try {
      await axios.patch(`${API}/admin/coupons/${c.id}`, { active: !c.active }, { withCredentials: true });
      coupons.refresh();
    } catch {
      toast.error("Failed to toggle");
    }
  };

  return (
    <main
      data-testid="admin-dashboard"
      className="min-h-screen flex bg-[#F8FAFC] dark:bg-[#020617] text-[#1E293B] dark:text-[#F8FAFC]"
    >
      <Sidebar active={active} onSelect={setActive} onLogout={logout} user={user} />

      <div className="flex-1 min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 border-b border-slate-200/70 dark:border-white/5 bg-white/80 dark:bg-[#020617]/80 backdrop-blur">
          <div className="px-6 sm:px-8 py-3.5 flex items-center gap-3">
            <h1 className="font-display text-lg font-semibold tracking-tight capitalize">
              {NAV.find((n) => n.id === active)?.label || active}
            </h1>
            <div className="ml-auto flex items-center gap-2 flex-1 max-w-md">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input
                  data-testid="admin-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  className="w-full rounded-full border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={refreshAll}
              data-testid="admin-refresh"
              className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/5"
              aria-label="Refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
            <Link
              to="/"
              data-testid="admin-open-site"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-2 text-xs hover:bg-slate-100 dark:hover:bg-white/5"
            >
              <ExternalLink className="h-3 w-3" /> Open site
            </Link>
          </div>
          {/* Mobile nav */}
          <div className="lg:hidden flex items-center gap-2 px-6 pb-3 overflow-x-auto">
            {NAV.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => setActive(n.id)}
                data-testid={`admin-nav-mobile-${n.id}`}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border ${
                  active === n.id
                    ? "bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] border-transparent"
                    : "border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300"
                }`}
              >
                {n.label}
              </button>
            ))}
          </div>
        </header>

        <div className="p-6 sm:p-8 space-y-6">
          {active === "overview" && (
            <>
              {/* Row 1 — Revenue */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold mb-3">
                  Revenue
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard testid="stat-revenue-today" label="Revenue Today" value={rupees(s.today_revenue || 0)} icon={Calendar} tone="emerald" />
                  <StatCard testid="stat-revenue-week" label="Revenue Last 7 Days" value={rupees(s.week_revenue || 0)} icon={TrendingUp} tone="emerald" />
                  <StatCard testid="stat-revenue-month" label="Revenue This Month" value={rupees(s.month_revenue || 0)} icon={TrendingUp} tone="emerald" />
                  <StatCard testid="stat-revenue-total" label="Total Revenue" value={rupees(s.total_revenue || 0)} icon={IndianRupee} tone="emerald" />
                </div>
              </div>

              {/* Row 2 — Sales */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold mb-3">
                  Sales
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard testid="stat-sales-today" label="Sales Today" value={s.today_sales || 0} icon={ShoppingCart} />
                  <StatCard testid="stat-sales-week" label="Sales Last 7 Days" value={s.week_sales || 0} icon={ShoppingCart} />
                  <StatCard testid="stat-sales-month" label="Sales This Month" value={s.month_sales || 0} icon={ShoppingCart} />
                  <StatCard testid="stat-aov" label="Avg Order Value" value={inr(s.avg_order_paise || 0)} icon={IndianRupee} tone="amber" />
                </div>
              </div>

              {/* Row 3 — Students & Package mix */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold mb-3">
                  Students & Package Mix
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard testid="stat-students" label="Total Students" value={s.total_students || 0} icon={GraduationCap} />
                  <StatCard testid="stat-premium" label="Premium Package Sales" value={s.premium_sales || 0} icon={Award} tone="amber" />
                  <StatCard testid="stat-basic" label="Basic Package Sales" value={s.basic_sales || 0} icon={Award} />
                  <StatCard testid="stat-elite" label="E-Lite Package Sales" value={s.elite_sales || 0} icon={Award} />
                </div>
              </div>

              {/* Row 4 — Recovery & Risk */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
                    Recovery & Risk
                  </div>
                  <button
                    type="button"
                    onClick={() => setActive("recovery")}
                    data-testid="kpi-open-recovery"
                    className="text-[11px] font-semibold text-[#2563EB] dark:text-[#06B6D4] hover:underline"
                  >
                    View list →
                  </button>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  <StatCard testid="stat-failed-count" label="Failed Payments" value={s.failed_sales || 0} icon={AlertTriangle} tone="rose" />
                  <StatCard testid="stat-failed-amount" label="Failed Amount" value={rupees(s.failed_revenue || 0)} icon={IndianRupee} tone="rose" />
                  <StatCard testid="stat-failure-rate" label="Failure Rate" value={`${s.failure_rate ?? 0}%`} icon={TrendingUp} tone="rose" />
                  <StatCard testid="stat-abandoned-count" label="Abandoned Checkouts" value={s.abandoned_count || 0} icon={Clock} tone="amber" />
                  <StatCard testid="stat-abandoned-value" label="Abandoned Value" value={rupees(s.abandoned_value || 0)} icon={IndianRupee} tone="amber" />
                </div>
              </div>

              {/* Charts row */}
              <div className="grid lg:grid-cols-3 gap-4">
                {/* Revenue Chart */}
                <div className="lg:col-span-2 rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-6 hairline">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
                    Last 14 Days
                  </div>
                  <h3 className="font-display text-lg font-semibold tracking-tight">Revenue Trend</h3>
                  <div className="h-64 mt-4" data-testid="revenue-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={s.revenue_14d || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: "currentColor" }} stroke="rgba(148,163,184,0.3)" />
                        <YAxis tick={{ fontSize: 11, fill: "currentColor" }} stroke="rgba(148,163,184,0.3)" tickFormatter={(v) => `₹${v}`} />
                        <Tooltip formatter={(v) => [rupees(v), "Revenue"]} contentStyle={{ borderRadius: 12, border: "1px solid rgba(148,163,184,0.25)" }} />
                        <Bar dataKey="revenue" fill="#06B6D4" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Package distribution pie */}
                <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-6 hairline">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
                    Sales Mix
                  </div>
                  <h3 className="font-display text-lg font-semibold tracking-tight">Package Distribution</h3>
                  <div className="h-64 mt-4" data-testid="package-distribution-chart">
                    {(s.elite_sales || s.basic_sales || s.premium_sales) ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { name: "E-Lite", value: s.elite_sales || 0, fill: "#64748B" },
                              { name: "Basic", value: s.basic_sales || 0, fill: "#2563EB" },
                              { name: "Premium", value: s.premium_sales || 0, fill: "#06B6D4" },
                            ]}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={3}
                          >
                            {["#64748B", "#2563EB", "#06B6D4"].map((c, i) => (
                              <Cell key={i} fill={c} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(148,163,184,0.25)" }} />
                          <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                        No sales yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {active === "leads" && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {leads.data.length} total leads
                </p>
                <button
                  type="button"
                  onClick={() => downloadCsv(leads.data, `leads-${Date.now()}.csv`)}
                  data-testid="admin-export-leads"
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-2 text-xs hover:bg-slate-100 dark:hover:bg-white/5"
                >
                  <Download className="h-3 w-3" /> Export CSV
                </button>
              </div>
              <Table
                testid="admin-leads-table"
                cols={["Name", "Email", "Phone", "Interest", "Source", "Created"]}
                rows={filter(leads.data, ["name", "email", "phone", "interest", "source"]).map((l) => [
                  l.name,
                  l.email,
                  l.phone || "—",
                  l.interest || "—",
                  l.source || "—",
                  new Date(l.created_at).toLocaleString(),
                ])}
                loading={leads.loading}
                empty="No leads yet."
              />
            </>
          )}

          {active === "orders" && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {orders.data.length} total orders · {paidOrders.length} paid
                </p>
                <button
                  type="button"
                  onClick={() => downloadCsv(orders.data, `orders-${Date.now()}.csv`)}
                  data-testid="admin-export-orders"
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-2 text-xs hover:bg-slate-100 dark:hover:bg-white/5"
                >
                  <Download className="h-3 w-3" /> Export CSV
                </button>
              </div>
              <Table
                testid="admin-orders-table"
                cols={["Customer", "Email", "Package", "Subtotal", "GST (18%)", "Total Paid", "Status", "Created"]}
                rows={filter(orders.data, ["name", "email", "package_name", "status"]).map((o) => [
                  o.name,
                  o.email,
                  o.package_name,
                  inr(o.subtotal_paise ?? o.amount_paise),
                  inr(o.gst_paise ?? 0),
                  <span key={`amt-${o.id}`} className="font-semibold text-[#0F172A] dark:text-white">
                    {inr(o.amount_paise)}
                  </span>,
                  <span
                    key={o.id}
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${
                      o.status === "paid"
                        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                        : o.status === "failed"
                        ? "bg-rose-500/15 text-rose-700 dark:text-rose-300"
                        : "bg-slate-200 dark:bg-white/10 text-slate-700 dark:text-slate-300"
                    }`}
                  >
                    {o.status}
                  </span>,
                  new Date(o.created_at).toLocaleString(),
                ])}
                loading={orders.loading}
                empty="No orders yet."
              />
            </>
          )}

          {active === "students" && <StudentsManager />}

          {active === "courses" && <CoursesManager />}

          {active === "training" && <TrainingManager />}

          {active === "testimonials" && <TestimonialsManager />}
          {active === "certification" && <CertificationManager />}

          {active === "pricing" && <PricingManager />}
          {active === "packages" && <PackagesManager />}

          {active === "settings" && <SettingsManager />}

          {active === "emails" && <EmailLogs />}

          {active === "recovery" && (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <StatCard testid="rec-stat-failed-count" label="Failed Payments" value={s.failed_sales || 0} icon={AlertTriangle} tone="rose" />
                <StatCard testid="rec-stat-failed-amount" label="Failed Amount" value={rupees(s.failed_revenue || 0)} icon={IndianRupee} tone="rose" />
                <StatCard testid="rec-stat-failure-rate" label="Failure Rate" value={`${s.failure_rate ?? 0}%`} icon={TrendingUp} tone="rose" />
                <StatCard testid="rec-stat-abandoned-count" label="Abandoned Checkouts" value={s.abandoned_count || 0} icon={Clock} tone="amber" />
                <StatCard testid="rec-stat-abandoned-value" label="Abandoned Value" value={rupees(s.abandoned_value || 0)} icon={IndianRupee} tone="amber" />
              </div>

              <div className="mt-2">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2">
                      <Clock className="h-4 w-4 text-amber-500" /> Abandoned Checkouts
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Orders left in checkout {">"} 15 min ago. Reach out via phone or email to recover the sale.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadCsv(abandoned.data || [], `abandoned-${Date.now()}.csv`)}
                    data-testid="admin-export-abandoned"
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-2 text-xs hover:bg-slate-100 dark:hover:bg-white/5"
                  >
                    <Download className="h-3 w-3" /> Export CSV
                  </button>
                </div>
                <Table
                  testid="admin-abandoned-table"
                  cols={["Customer", "Email", "Phone", "Package", "Cart Value", "Coupon", "Abandoned"]}
                  rows={filter(abandoned.data || [], ["name", "email", "phone", "package_name", "coupon_code"]).map((o) => [
                    o.name || "—",
                    <a key={`em-${o.id}`} href={`mailto:${o.email}`} className="text-[#2563EB] dark:text-[#06B6D4] hover:underline inline-flex items-center gap-1">
                      <Mail className="h-3 w-3" /> {o.email}
                    </a>,
                    o.phone ? (
                      <a key={`ph-${o.id}`} href={`tel:${o.phone}`} className="text-[#2563EB] dark:text-[#06B6D4] hover:underline font-mono">
                        {o.phone}
                      </a>
                    ) : "—",
                    o.package_name || "—",
                    inr(o.amount_paise),
                    o.coupon_code || "—",
                    new Date(o.created_at).toLocaleString(),
                  ])}
                  loading={abandoned.loading}
                  empty="No abandoned checkouts in the last 30 days. 🎉"
                />
              </div>

              <div className="mt-2">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-rose-500" /> Failed Payments
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Payments declined by the gateway. Reach out and offer an alternate payment method.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadCsv(failed.data || [], `failed-payments-${Date.now()}.csv`)}
                    data-testid="admin-export-failed"
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-2 text-xs hover:bg-slate-100 dark:hover:bg-white/5"
                  >
                    <Download className="h-3 w-3" /> Export CSV
                  </button>
                </div>
                <Table
                  testid="admin-failed-table"
                  cols={["Customer", "Email", "Phone", "Package", "Amount", "Reason", "When"]}
                  rows={filter(failed.data || [], ["name", "email", "phone", "package_name", "failure_reason"]).map((o) => [
                    o.name || "—",
                    <a key={`em-${o.id}`} href={`mailto:${o.email}`} className="text-[#2563EB] dark:text-[#06B6D4] hover:underline inline-flex items-center gap-1">
                      <Mail className="h-3 w-3" /> {o.email}
                    </a>,
                    o.phone ? (
                      <a key={`ph-${o.id}`} href={`tel:${o.phone}`} className="text-[#2563EB] dark:text-[#06B6D4] hover:underline font-mono">
                        {o.phone}
                      </a>
                    ) : "—",
                    o.package_name || "—",
                    inr(o.amount_paise),
                    <span key={`r-${o.id}`} className="text-xs text-rose-600 dark:text-rose-400">
                      {o.failure_reason || "Declined"}
                    </span>,
                    new Date(o.created_at).toLocaleString(),
                  ])}
                  loading={failed.loading}
                  empty="No failed payments. 🎉"
                />
              </div>
            </>
          )}

          {active === "coupons" && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {coupons.data.length} coupons configured
                </p>
                {!showCouponForm && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingCoupon(null);
                      setShowCouponForm(true);
                    }}
                    data-testid="coupon-create-btn"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-4 py-2 text-xs font-semibold hover:-translate-y-0.5 transition-transform"
                  >
                    <Plus className="h-3 w-3" /> New Coupon
                  </button>
                )}
              </div>

              {showCouponForm && (
                <CouponForm
                  initial={editingCoupon}
                  onSave={handleCouponSave}
                  onCancel={() => {
                    setShowCouponForm(false);
                    setEditingCoupon(null);
                  }}
                />
              )}

              <Table
                testid="admin-coupons-table"
                cols={["Code", "Discount", "Applies to", "Uses", "Expires", "Status", "Actions"]}
                rows={filter(coupons.data, ["code", "applies_to"]).map((c) => [
                  <span key={c.id} data-testid={`coupon-row-${c.code}`} className="font-mono font-bold text-sm">{c.code}</span>,
                  c.discount_type === "percent" ? `${c.discount_value}% OFF` : `₹${(c.discount_value / 100).toLocaleString("en-IN")} OFF`,
                  c.applies_to,
                  c.max_uses ? `${c.used_count} / ${c.max_uses}` : `${c.used_count} / ∞`,
                  c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "Never",
                  <span
                    key={`s-${c.id}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${
                      c.active
                        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                        : "bg-slate-200 dark:bg-white/10 text-slate-700 dark:text-slate-300"
                    }`}
                  >
                    {c.active ? "Active" : "Inactive"}
                  </span>,
                  <div key={`a-${c.id}`} className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleCouponToggle(c)}
                      data-testid={`coupon-toggle-${c.code}`}
                      className="h-7 w-7 inline-flex items-center justify-center rounded-full border border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/5"
                      title={c.active ? "Deactivate" : "Activate"}
                    >
                      <Power className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingCoupon(c);
                        setShowCouponForm(true);
                      }}
                      data-testid={`coupon-edit-${c.code}`}
                      className="h-7 w-7 inline-flex items-center justify-center rounded-full border border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/5"
                      title="Edit"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCouponDelete(c.id)}
                      data-testid={`coupon-delete-${c.code}`}
                      className="h-7 w-7 inline-flex items-center justify-center rounded-full border border-slate-200 dark:border-white/10 hover:bg-rose-500/10 hover:text-rose-500"
                      title="Delete"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>,
                ])}
                loading={coupons.loading}
                empty="No coupons yet. Create your first coupon to start running launch offers."
              />
            </>
          )}

          {active === "analytics" && (
            <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-10 text-center">
              <BarChart3 className="h-10 w-10 mx-auto text-slate-400" />
              <h3 className="mt-4 font-display text-xl font-semibold">Deeper analytics coming soon</h3>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                Cart abandonment, course conversion funnel, lead-source attribution and more — once we instrument the events.
              </p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
